import React, { Fragment, useState } from 'react';
import Alpha from '../color-alpha';
import EditableInput from '../color-editable-input';
import EditableInputHSLA from '../color-editable-input-hsla';
import EditableInputRGBA from '../color-editable-input-rgba';
import Github, { GithubPlacement, GithubProps } from '../color-github-picker';
import Hue from '../color-hue';
import Saturation from '../color-saturation';
import {
    color as handleColor,
    hexToHsva,
    HsvaColor,
    hsvaToHex,
    hsvaToHexa,
    hsvaToRgbaString,
    validHex,
} from '../utils/color-convert';
import Arrow from './arrow';

export enum ChromeInputType {
    HEXA = 'hexa',
    RGBA = 'rgba',
    HSLA = 'hsla',
}

export interface ChromePickerProps extends Omit<GithubProps, 'colors'> {
    inputType?: ChromeInputType;
}

const ChromePicker = React.forwardRef<HTMLDivElement, ChromePickerProps>((props, ref) => {
    const {
        prefixCls = 'c-color-chrome-picker',
        className,
        style,
        color,
        inputType = ChromeInputType.RGBA,
        rectProps = {},
        onChange,
        ...other
    } = props;
    const hsva = (
        typeof color === 'string' && validHex(color) ? hexToHsva(color) : color || { h: 0, s: 0, l: 0, a: 0 }
    ) as HsvaColor;
    const handleChange = (hsv: HsvaColor) => onChange && onChange(handleColor(hsv));
    const [type, setType] = useState(inputType);
    const handleClick = () => {
        if (type === ChromeInputType.RGBA) {
            setType(ChromeInputType.HSLA);
        }
        if (type === ChromeInputType.HSLA) {
            setType(ChromeInputType.HEXA);
        }
        if (type === ChromeInputType.HEXA) {
            setType(ChromeInputType.RGBA);
        }
    };
    const labelStyle: React.CSSProperties = { paddingTop: 6 };
    const inputStyle: React.CSSProperties = { textAlign: 'center', paddingTop: 4, paddingBottom: 4 };
    return (
        <Github
            ref={ref}
            color={hsva}
            style={{ borderRadius: 0, flexDirection: 'column', width: 230, padding: 0, ...style }}
            colors={undefined}
            className={[prefixCls, className].filter(Boolean).join(' ')}
            placement={GithubPlacement.TopLeft}
            {...other}
            addonAfter={
                <Fragment>
                    <Saturation
                        hsva={hsva}
                        style={{ width: '100%', height: 130 }}
                        onChange={(newColor) => {
                            handleChange({ ...hsva, ...newColor, a: hsva.a });
                        }}
                    />
                    <div style={{ padding: 15, display: 'flex', alignItems: 'center' }}>
                        <Alpha
                            width={24}
                            height={24}
                            hsva={hsva}
                            radius={2}
                            style={{
                                marginRight: 15,
                                borderRadius: '50%',
                            }}
                            bgProps={{ style: { background: 'transparent' } }}
                            innerProps={{
                                style: {
                                    borderRadius: '50%',
                                    background: hsvaToRgbaString(hsva),
                                    boxShadow: 'rgb(0 0 0 / 25%) 0px 0px 1px inset',
                                },
                            }}
                            pointer={() => <Fragment />}
                        />
                        <div style={{ flex: 1 }}>
                            <Hue
                                hsva={hsva}
                                style={{ width: '100%' }}
                                bgProps={{
                                    style: { borderRadius: 2 },
                                }}
                                onChange={(newHue) => {
                                    handleChange({ ...hsva, ...newHue });
                                }}
                            />
                            <Alpha
                                hsva={hsva}
                                style={{ marginTop: 10 }}
                                bgProps={{
                                    style: { borderRadius: 2 },
                                }}
                                onChange={(newAlpha) => {
                                    handleChange({ ...hsva, ...newAlpha });
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '0 15px 15px 15px',
                            userSelect: 'none',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            {type == ChromeInputType.RGBA && (
                                <EditableInputRGBA
                                    hsva={hsva}
                                    rProps={{ labelStyle, inputStyle }}
                                    gProps={{ labelStyle, inputStyle }}
                                    bProps={{ labelStyle, inputStyle }}
                                    aProps={{ labelStyle, inputStyle }}
                                    onChange={(reColor) => handleChange(reColor.hsva)}
                                />
                            )}
                            {type === ChromeInputType.HEXA && (
                                <EditableInput
                                    label="HEX"
                                    labelStyle={labelStyle}
                                    inputStyle={inputStyle}
                                    value={
                                        hsva.a > 0 && hsva.a < 1
                                            ? hsvaToHexa(hsva).toLocaleUpperCase()
                                            : hsvaToHex(hsva).toLocaleUpperCase()
                                    }
                                    onChange={(_, value) => {
                                        if (typeof value === 'string') {
                                            handleChange(hexToHsva(/^#/.test(value) ? value : `#${value}`));
                                        }
                                    }}
                                />
                            )}
                            {type === ChromeInputType.HSLA && (
                                <EditableInputHSLA
                                    hsva={hsva}
                                    hProps={{ labelStyle, inputStyle }}
                                    sProps={{ labelStyle, inputStyle }}
                                    lProps={{ labelStyle, inputStyle }}
                                    aProps={{ labelStyle, inputStyle }}
                                    onChange={(reColor) => handleChange(reColor.hsva)}
                                />
                            )}
                        </div>
                        <Arrow onClick={handleClick} />
                    </div>
                </Fragment>
            }
            rectRender={({ key }) => <Fragment key={key} />}
        />
    );
});

ChromePicker.displayName = 'ChromePicker';

export default ChromePicker;
